import React, { useState, useEffect } from 'react';
import { authHelpers } from '../global/supabase';
import '../global/styles.css';

function EmailConfirmation({ userEmail, onConfirmed, onGoToLogin }) {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isChecking, setIsChecking] = useState(true); // Start as true for initial check
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState('');

  // Check email confirmation status
  const checkConfirmationStatus = async () => {
    setIsChecking(true);
    setError('');

    try {
      // Get current session
      const { data: { session } } = await authHelpers.supabase.auth.getSession();

      console.log('Checking confirmation status:', {
        hasSession: !!session,
        email: session?.user?.email,
        emailConfirmedAt: session?.user?.email_confirmed_at
      });

      // If user has a session and email is confirmed
      if (session?.user?.email_confirmed_at) {
        console.log('Email confirmed!');
        setIsConfirmed(true);
        if (onConfirmed) {
          onConfirmed();
        }
      } else if (session?.user && !session.user.email_confirmed_at) {
        console.log('User authenticated but email not confirmed yet');
        // User is authenticated but email not confirmed (shouldn't happen in normal flow)
      } else {
        console.log('No session found - waiting for user to click email link');
      }
    } catch (error) {
      console.error('Error checking confirmation:', error);
      setError('Error checking confirmation status');
    }

    setIsChecking(false);
    setLastChecked(new Date());
  };

  // Auto-check every 5 seconds
  useEffect(() => {
    // Initial check
    checkConfirmationStatus();

    const interval = setInterval(() => {
      if (!isConfirmed) {
        checkConfirmationStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isConfirmed]);

  const handleManualRefresh = () => {
    checkConfirmationStatus();
  };

  const formatTime = (date) => {
    return date ? date.toLocaleTimeString() : '';
  };

  return (
    <div className="container-center">
      <div className="card card-center card-large">
        <div className="logo-container">
          <div className={`status-icon ${isConfirmed ? 'success' : 'pending'}`}>
            {isConfirmed ? '✓' : '📧'}
          </div>
          <h2 className="title-auth">Check Your Email</h2>
        </div>

        <div className="content">
          <p className="subtitle">
            We've sent a confirmation link to:
          </p>
          <p className="email-display">{userEmail}</p>

          {error && (
            <div className="message error" style={{ marginTop: '1rem' }}>
              {error}
            </div>
          )}

          {!isConfirmed ? (
            <div>
              <p className="subtitle">Please check your email and click the confirmation link to activate your account.</p>
              <div className="status">
                <span className="status-dot pending"></span>
                <span>Waiting for confirmation...</span>
                {isChecking && <span className="spinner">⟳</span>}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-success">Email confirmed successfully!</p>
              <div className="status">
                <span className="status-dot success"></span>
                <span>Account activated</span>
              </div>
            </div>
          )}

          {lastChecked && (
            <p className="text-small">
              Last checked: {formatTime(lastChecked)}
            </p>
          )}
        </div>

        <div className="form-group">
          {!isConfirmed ? (
            <button
              onClick={handleManualRefresh}
              className="btn btn-secondary btn-full"
              disabled={isChecking}
            >
              {isChecking ? 'Checking...' : 'Refresh Status'}
            </button>
          ) : (
            <button
              onClick={onGoToLogin}
              className="btn btn-primary btn-full"
            >
              Continue to Login
            </button>
          )}
        </div>

        <div className="help">
          <p>Didn't receive the email?</p>
          <ul>
            <li>Check your spam/junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Wait a few minutes - emails can take time to arrive</li>
          </ul>
        </div>
      </div>

      <footer className="footer">
        <p className="text-small">NarcoticTrack © 2025 NarcoticTrack Inventory System, LLC</p>
      </footer>
    </div>
  );
}

export default EmailConfirmation;
